import { Component, OnInit } from '@angular/core';
import { IProduct, IProductCompany, IProductCompanyTopCompany, IProductKpi, IProductOrdersKPI } from '../../../models/products.models';
import { AppState } from 'src/app/core/app.state';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { ActivatedRoute, Router } from '@angular/router';
import { SetCurrentElementId } from 'src/app/shared/store/shared.actions';
import {
  ClearKpisProduct,
  GetProductById,
  GetProductByIdError,
  GetProductByIdSuccess,
  GetProductCompanies,
  GetProductCompaniesError,
  GetProductCompaniesSuccess,
  GetProductOrdersKPI,
} from '../../../store/produits.actions';
import { Observable, Subscription, tap } from 'rxjs';
import { DATE_FORMAT } from 'src/app/config/global.config';
import { ChartConfiguration } from 'chart.js';
import { FormControl } from '@angular/forms';

export interface IVariantElement {
  variant_id: number;
  values: string[];
}

@Component({
  selector: 'app-insights',
  templateUrl: './insights.component.html',
  styleUrls: ['./insights.component.scss']
})
export class InsightsComponent implements OnInit {

  product!: IProduct;
  produitId!: number;
  private subscriptions: Subscription[] = [];
  productCompanies$: Observable<IProductCompany | null> = this.store.select((state)=> state.products.productCompanies);
  productOrdersKPI$: Observable<IProductOrdersKPI | null> = this.store.select((state)=> state.products.productOrdersKPI);
  productOrdersKPI: IProductOrdersKPI | null = null;
  productCompaniesData: IProductCompany | null = null;
  productCompanies: IProductCompanyTopCompany[] = []
  productKpi: IProductKpi | null = null;
  DATE_FORMAT = DATE_FORMAT;
  variantControl = new FormControl('all_variants');
  variants: IVariantElement[] = [];
  private allVariantsDatasets: any[] = [];
  private readonly monthNames: string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private route: ActivatedRoute,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.produitId = +this.route.snapshot.paramMap.get('id')!;
    if (this.produitId) {
      this.store.dispatch(GetProductOrdersKPI({ id: this.produitId }));
      this.store.dispatch(GetProductCompanies({ id: this.produitId }));
    }

    const sub3 = this.productCompanies$.subscribe((companies) => {
      if (companies) {
        this.productCompaniesData = companies;
        this.productCompanies = companies.top_companies;
      }
    });

      const sub4 = this.actions$
      .pipe(
        ofType(
          GetProductCompaniesError
        ),
        tap(() => {
          // this.cancel(true);
        })
      )
      .subscribe();

    const sub5 = this.productOrdersKPI$
      .pipe(
        tap((kpis) => {
          if (!kpis) { return; }
          this.productOrdersKPI = kpis;
          this.productKpi = kpis.kpi;
          const currentYear = new Date().getFullYear().toString();
          const currentYearMonths = kpis.orders_value_per_month.filter(k => 
            k.month && k.month.startsWith(currentYear)
          );
          const labels = currentYearMonths.map(k => {
            const monthNumber = k.month ? parseInt(k.month.split('-')[1], 10) : 0;
            return this.monthNames[monthNumber - 1];
          });
          const data = currentYearMonths.map(k => k.total_orders_value);
          
          // Create a map of month values for alignment
          const monthKeys = currentYearMonths.map(k => k.month);
          
          const datasets: any[] = [];
          // Add variant datasets if variants exist
          if (kpis.variants && kpis.variants.length > 0) {
            this.variants = kpis.variants;
            const variantColors = this.generateVariantColors(kpis.variants.length);
            // datasets should only have variants - Chart.js will stack them automatically
            kpis.variants.forEach((v, index) => {
              // Align variant data with main data by matching month keys
              const variantData = monthKeys.map(monthKey => {
                const variantMonth = v.orders_value_per_month.find(m => m.month === monthKey);
                return variantMonth ? variantMonth.total_orders_value : 0;
              });
              datasets.push({
                label: v.values.join(' / '),
                data: variantData,
                backgroundColor: variantColors[index].backgroundColor,
                borderColor: variantColors[index].borderColor,
                borderWidth: 1,
                variantId: v.variant_id
              });
            });
            // Store all variants datasets for filtering
            this.allVariantsDatasets = [...datasets];
            // Set default value to "all_variants" if not already set
            if (!this.variantControl.value) {
              this.variantControl.setValue('all_variants', { emitEvent: false });
            }
            // Show legend when variants exist and enable stacking
            const existingPlugins = this.barChartOptions?.plugins ?? {};
            this.barChartOptions = {
              ...this.barChartOptions,
              scales: {
                x: {
                  stacked: true
                },
                y: {
                  stacked: true,
                  min: 0
                }
              },
              plugins: {
                ...existingPlugins,
                legend: {
                  display: true,
                  position: 'top',
                },
              }
            };
          } else {
            datasets.push({
              label: 'Orders Value',
              data,
              backgroundColor: '#74D3FF',
              borderColor: '#004768',
              borderWidth: 1
            });
            // Hide legend when no variants and disable stacking
            const existingPlugins = this.barChartOptions?.plugins ?? {};
            this.barChartOptions = {
              ...this.barChartOptions,
              scales: {
                x: {
                  stacked: false
                },
                y: {
                  stacked: false,
                  min: 0
                }
              },
              plugins: {
                ...existingPlugins,
                legend: {
                  display: false,
                  position: 'top',
                },
              }
            };
          }

          this.barChartData = {
            labels,
            datasets
          };
        })
      )
      .subscribe();

    const sub6 = this.variantControl.valueChanges.subscribe((value) => {
      if (!this.productOrdersKPI || !this.productOrdersKPI.variants || this.productOrdersKPI.variants.length === 0) {
        return;
      }

      const currentYear = new Date().getFullYear().toString();
      const currentYearMonths = this.productOrdersKPI.orders_value_per_month.filter(k => 
        k.month && k.month.startsWith(currentYear)
      );
      const labels = currentYearMonths.map(k => {
        const monthNumber = k.month ? parseInt(k.month.split('-')[1], 10) : 0;
        return this.monthNames[monthNumber - 1];
      });
      // Create a map of month values for alignment
      const monthKeys = currentYearMonths.map(k => k.month);
      let datasets: any[] = [];

      if (value === 'all_variants') {
        // Show all variants with stacking enabled
        datasets = [...this.allVariantsDatasets];
        this.productKpi = this.productOrdersKPI.kpi;
        this.barChartOptions = {
          ...this.barChartOptions,
          scales: {
            x: {
              stacked: true
            },
            y: {
              stacked: true,
              min: 0
            }
          }
        };

        this.productCompanies = this.productCompaniesData?.top_companies || [];
      } else {
        // Show only selected variant with stacking disabled
        const selectedVariantId = typeof value === 'string' ? parseInt(value, 10) : value;
        const selectedVariant = this.productOrdersKPI.variants.find(v => v.variant_id === selectedVariantId);
        if (selectedVariant) {
          this.productKpi = selectedVariant.kpis;
          this.productCompanies = this.productCompaniesData?.variants?.find(v => v.variant_id === selectedVariantId)?.top_companies || [];
          // Find the original dataset to preserve colors
          const originalDataset = this.allVariantsDatasets.find(d => d.variantId === selectedVariantId);
          // Align variant data with main data by matching month keys
          const variantData = monthKeys.map(monthKey => {
            const variantMonth = selectedVariant.orders_value_per_month.find(m => m.month === monthKey);
            return variantMonth ? variantMonth.total_orders_value : 0;
          });
          datasets.push({
            label: selectedVariant.values.join(', '),
            data: variantData,
            backgroundColor: originalDataset?.backgroundColor || '#74D3FF',
            borderColor: originalDataset?.borderColor || '#004768',
            borderWidth: 1,
            variantId: selectedVariant.variant_id
          });
        }
        this.barChartOptions = {
          ...this.barChartOptions,
          scales: {
            x: {
              stacked: false
            },
            y: {
              stacked: false,
              min: 0
            }
          }
        };
      }

      this.barChartData = {
        labels,
        datasets
      };
    });

    this.subscriptions.push(sub3, sub4, sub5, sub6);
  }

  barChartData: ChartConfiguration['data'] = {
      labels: [],
      datasets: [
        {
          label: 'Orders Value',
          data: [],
          backgroundColor: '#74D3FF',
          borderColor: '#004768',
          borderWidth: 1
        }
      ]
    };
  
    barChartOptions: ChartConfiguration['options'] = {
      responsive: true,
      // Customize scales, plugins, etc. as needed
      scales: {
        x: {
          stacked: true
        },
        y: {
          stacked: true,
          min: 0
        }
      },
      plugins: {
        legend: {
          display: false,
          position: 'top',
        },
      }
    };
  
  generateVariantColors(count: number): Array<{ backgroundColor: string; borderColor: string }> {
    const colors: Array<{ backgroundColor: string; borderColor: string }> = [];
    const baseColors = [
      { bg: '#77DBB3', border: '#006F43)' },
      { bg: '#74D3FF', border: '#004768' },
      { bg: '#FFF2D0', border: '#765700' },
      { bg: 'rgba(255, 99, 132, 0.6)', border: 'rgba(255, 99, 132, 1)' }, // Pink
      { bg: 'rgba(153, 102, 255, 0.6)', border: 'rgba(153, 102, 255, 1)' }, // Purple
      { bg: 'rgba(54, 162, 235, 0.6)', border: 'rgba(54, 162, 235, 1)' }, // Blue
      { bg: 'rgba(255, 206, 86, 0.6)', border: 'rgba(255, 206, 86, 1)' }, // Yellow
      { bg: 'rgba(201, 203, 207, 0.6)', border: 'rgba(201, 203, 207, 1)' }, // Grey
    ];

    for (let i = 0; i < count; i++) {
      const colorIndex = i % baseColors.length;
      colors.push({
        backgroundColor: baseColors[colorIndex].bg,
        borderColor: baseColors[colorIndex].border
      });
    }

    return colors;
  }

  noChartData(): boolean {
    const noKpis = !this.productOrdersKPI || this.productOrdersKPI.orders_value_per_month.length === 0;
    const noMainData = this.productOrdersKPI?.orders_value_per_month.every(k => k.total_orders_value === 0);
    
    // Check if variants have data
    const hasVariantData = this.productOrdersKPI?.variants?.some(v => 
      v.orders_value_per_month.some(m => m.total_orders_value > 0)
    ) || false;

    return noKpis || (noMainData && !hasVariantData) || !this.productOrdersKPI;
  }

  cancel(keepParams = true) {
    this.router.navigate(['inventory'], {
      queryParamsHandling: keepParams ? 'merge': ''
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.store.dispatch(ClearKpisProduct());
  }

}
